import { format, isValid } from 'date-fns'
import { Controller, useFormContext } from 'react-hook-form'
import { requiredFn } from 'utility/validators'
import { todayDateInputValue, validateFutureExpiration } from '../expirationDate'
import { Field } from '../Field'
import { FileField } from '../FileField'
import { InputStyled, RowStyled, SectionStyled } from '../onboardingStyles'
import { ICoachProfileFormValues } from '../types'

interface ICredentialsSection {
  existingCardUrl?: string | null
  existingPhotoUrl?: string | null
}

export const CredentialsSection = ({ existingCardUrl, existingPhotoUrl }: ICredentialsSection) => {
  const {
    register,
    control,
    formState: { errors }
  } = useFormContext<ICoachProfileFormValues>()

  return (
    <SectionStyled id="section-credentials">
      <div className="section-head">
        <div className="heading-block">
          <span className="num" aria-hidden="true">
            02
          </span>
          <div>
            <h2>Credentials</h2>
            <div className="blurb">We verify these against the CDR registry before approval.</div>
          </div>
        </div>
      </div>

      <Field
        htmlFor="credentials"
        label="Credentials"
        required
        help="As you'd like them to appear after your name."
        error={errors.credentials?.message}
      >
        <InputStyled
          id="credentials"
          placeholder="e.g., MS, RD, CNS"
          aria-invalid={!!errors.credentials || undefined}
          {...register('credentials', { validate: requiredFn })}
        />
      </Field>

      <RowStyled>
        <Field
          htmlFor="cdrCnsNumber"
          label="CDR / CNS number"
          required
          error={errors.cdrCnsNumber?.message}
        >
          <InputStyled
            id="cdrCnsNumber"
            placeholder="e.g., 1234567"
            aria-invalid={!!errors.cdrCnsNumber || undefined}
            {...register('cdrCnsNumber', { validate: requiredFn })}
          />
        </Field>
        <Field
          htmlFor="cdrCnsExpirationDate"
          label="Expiration date"
          required
          error={errors.cdrCnsExpirationDate?.message}
        >
          <Controller
            control={control}
            name="cdrCnsExpirationDate"
            rules={{
              validate: (value) => validateFutureExpiration(value, 'Add the expiration date.')
            }}
            render={({ field: { onChange, value } }) => (
              <InputStyled
                as="input"
                type="date"
                id="cdrCnsExpirationDate"
                aria-invalid={!!errors.cdrCnsExpirationDate || undefined}
                min={todayDateInputValue()}
                value={value && isValid(value) ? format(value, 'yyyy-MM-dd') : ''}
                onChange={(event) =>
                  onChange(event.target.value ? new Date(`${event.target.value}T00:00:00`) : null)
                }
              />
            )}
          />
        </Field>
      </RowStyled>

      <Field
        htmlFor="cdrCnsCard-input"
        label="CDR / CNS license card"
        required
        help="A clear photo or PDF of both sides. Max 10MB."
        error={errors.cdrCnsCard?.message}
      >
        <Controller
          control={control}
          name="cdrCnsCard"
          rules={{
            validate: (value) =>
              value || existingCardUrl ? undefined : 'Please upload your license card.'
          }}
          render={({ field: { onChange, value } }) => (
            <FileField
              inputId="cdrCnsCard-input"
              accept="application/pdf,image/png,image/jpeg"
              hint="PDF, PNG, JPG · up to 10MB"
              icon="document"
              file={value}
              existingFileUrl={existingCardUrl}
              invalid={!!errors.cdrCnsCard}
              onSelect={onChange}
            />
          )}
        />
      </Field>

      <Field
        htmlFor="photo-input"
        label="Headshot"
        required
        help="Friendly, well-lit photo from the shoulders up. JPG or PNG."
        error={errors.photo?.message}
      >
        <Controller
          control={control}
          name="photo"
          rules={{
            validate: (value) =>
              value || existingPhotoUrl ? undefined : 'Please upload a headshot.'
          }}
          render={({ field: { onChange, value } }) => (
            <FileField
              inputId="photo-input"
              accept="image/png,image/jpeg"
              hint="PNG or JPG · up to 5MB"
              icon="photo"
              file={value}
              existingFileUrl={existingPhotoUrl}
              invalid={!!errors.photo}
              onSelect={onChange}
            />
          )}
        />
      </Field>
    </SectionStyled>
  )
}
